import React, { useState } from 'react';
import { Save, X, AlertTriangle, Zap, Shield, Crown, Eye } from 'lucide-react';
// NEW: Import specific API modules
import { campaignAPI, facilityAPI, squadAPI } from '../api';

const UNIVERSES = {
  vanilla: {
    id: 'vanilla',
    name: 'Standard Operations',
    description: 'Classic tactical command experience',
    icon: Shield,
    color: 'blue',
    preview: 'Military precision and strategic depth'
  },
  devastators: {
    id: 'devastators',
    name: 'The Devastators',
    description: 'Post-apocalyptic dieselpunk warfare',
    icon: Zap,
    color: 'amber',
    preview: 'Fight against biomechanical machines in a smoke-choked wasteland'
  },
  majestic: {
    id: 'majestic',
    name: 'Majestic Echelon',
    description: 'Elite covert operations protocol',
    icon: Crown,
    color: 'emerald',
    preview: 'Sophisticated strategy and calculated precision'
  },
  archbound: {
    id: 'archbound',
    name: 'Archbound Legacy',
    description: 'Eldritch horror and ancient mysteries',
    icon: Eye,
    color: 'purple',
    preview: 'Confront cosmic gods and contain interdimensional threats'
  }
};

const NewCampaignModal = ({ isOpen, onClose, onCreate, userId }) => {
  const [name, setName] = useState('');
  const [difficulty, setDifficulty] = useState('normal');
  const [theme, setTheme] = useState('vanilla');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  // Random name generator for initial squad
  const generateRandomName = () => {
    const firstNames = [
      'James', 'Maria', 'John', 'Sarah', 'Michael', 'Lisa', 'David', 'Jennifer',
      'Robert', 'Emily', 'William', 'Ashley', 'Thomas', 'Jessica', 'Richard', 'Amanda',
      'Daniel', 'Nicole', 'Matthew', 'Melissa', 'Christopher', 'Rebecca', 'Andrew', 'Laura'
    ];
    const lastNames = [
      'Anderson', 'Brooks', 'Carter', 'Davis', 'Evans', 'Foster', 'Garcia', 'Harris',
      'Jackson', 'King', 'Lee', 'Martinez', 'Nelson', 'Ortiz', 'Parker', 'Quinn',
      'Rodriguez', 'Smith', 'Taylor', 'Walker', 'White', 'Young', 'Zhang', 'Mitchell'
    ];
    
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    return `${firstName} ${lastName}`;
  };

  // Initialize campaign with starting facilities and squad
  const initializeCampaign = async (campaignId) => {
    try {
      // 1. Create starting facility - Command Center Level 1 at grid position (2, 2)
      await facilityAPI.createFacility(
        campaignId,
        'command',
        'Command Center',
        2, // grid_row
        2, // grid_col
        1  // level
      );
      console.log('✓ Created Command Center');

      // 2. Create initial squad (4 soldiers with random names)
      const initialSquad = [
        { rank: 'Sergeant', missions: 0 },
        { rank: 'Corporal', missions: 0 },
        { rank: 'Private', missions: 0 },
        { rank: 'Private', missions: 0 }
      ];

      for (const soldier of initialSquad) {
        await squadAPI.createSquadMember(
          campaignId,
          generateRandomName(),
          soldier.rank,
          {
            health: 100,
            max_health: 100,
            status: 'Ready',
            missions_completed: soldier.missions
          }
        );
      }
      console.log('✓ Created initial squad of 4 soldiers');

      // 3. Research tree starts at zero (no initial research needed)
      console.log('✓ Research tree initialized (empty)');

      // 4. Missions require no initialization
      console.log('✓ Mission system ready');

      console.log('Campaign initialization complete!');
    } catch (error) {
      console.error('Error initializing campaign:', error);
      throw new Error('Failed to initialize campaign data');
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Campaign name is required.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (userId) {
        // Create campaign via API with theme
        const campaign = await campaignAPI.createCampaign(userId, name, {
          difficulty,
          theme, // Add theme to campaign data
          day: 1,
          threat_level: 1,
          supplies: 100,
          intel: 0,
          reputation: 0,
        });
        
        console.log('Campaign created:', campaign);
        console.log('Campaign ID:', campaign.id);
        console.log('Theme:', campaign.theme);

        // Ensure we have a valid campaign ID before initializing
        if (!campaign.id) {
          throw new Error('Campaign created but no ID returned');
        }

        // Initialize campaign with starting data
        await initializeCampaign(campaign.id);
        
        // Pass campaign with theme to parent
        onCreate({ ...campaign, theme });
      } else {
        // Fallback to local state if no userId
        const now = new Date().toISOString();
        const baseState = {
          id: crypto.randomUUID(),
          user_id: null,
          name,
          difficulty,
          theme, // Include theme in local state
          day: 1,
          threat_level: 1,
          supplies: 100,
          intel: 0,
          reputation: 0,
          created_at: now,
          last_played: now,
          total_playtime: 0,
          is_active: true,
          game_over: false,
          victory: false,
        };

        onCreate(baseState);
      }
    } catch (err) {
      setError(err.message || 'Failed to create campaign. Please try again.');
      setIsLoading(false);
    }
  };

  const selectedUniverse = UNIVERSES[theme];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-lg w-full max-w-3xl p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-300"
        >
          <X size={20} />
        </button>

        <h2 className="text-2xl font-bold text-center text-blue-400 mb-6">
          NEW CAMPAIGN
        </h2>

        <div className="space-y-6">
          {/* Universe Selection */}
          <div>
            <label className="text-sm text-gray-400 font-mono mb-3 block">SELECT UNIVERSE</label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.values(UNIVERSES).map((universe) => {
                const Icon = universe.icon;
                const isSelected = theme === universe.id;
                const colorClasses = {
                  blue: 'border-blue-500 bg-blue-500/10',
                  amber: 'border-amber-500 bg-amber-500/10',
                  emerald: 'border-emerald-500 bg-emerald-500/10',
                  purple: 'border-purple-500 bg-purple-500/10'
                };
                const textColors = {
                  blue: 'text-blue-400',
                  amber: 'text-amber-400',
                  emerald: 'text-emerald-400',
                  purple: 'text-purple-400'
                };

                return (
                  <button
                    key={universe.id}
                    type="button"
                    onClick={() => setTheme(universe.id)}
                    disabled={isLoading}
                    className={`
                      p-4 rounded-lg border-2 transition-all duration-200
                      ${isSelected 
                        ? colorClasses[universe.color] 
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                      }
                      ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <Icon 
                      className={`mx-auto mb-2 ${isSelected ? textColors[universe.color] : 'text-gray-500'}`} 
                      size={32} 
                    />
                    <h3 className={`font-bold text-sm mb-1 ${isSelected ? textColors[universe.color] : 'text-gray-400'}`}>
                      {universe.name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {universe.description}
                    </p>
                  </button>
                );
              })}
            </div>
            
            {/* Universe Preview */}
            <div className="mt-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
              <p className="text-xs text-gray-400 text-center italic">
                {selectedUniverse.preview}
              </p>
            </div>
          </div>

          {/* Campaign Name */}
          <div>
            <label className="text-sm text-gray-400 font-mono">CAMPAIGN NAME</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              placeholder="Enter campaign name..."
              className="w-full mt-1 bg-gray-800 border border-gray-700 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              disabled={isLoading}
            />
          </div>

          {/* Difficulty */}
          <div>
            <label className="text-sm text-gray-400 font-mono">DIFFICULTY</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full mt-1 bg-gray-800 border border-gray-700 rounded p-2 text-white focus:outline-none focus:border-blue-500"
              disabled={isLoading}
            >
              <option value="easy">EASY - For relaxed play</option>
              <option value="normal">NORMAL - Standard challenge</option>
              <option value="hard">HARD - For veterans</option>
            </select>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertTriangle size={16} /> {error}
            </div>
          )}
        </div>

        <button
          onClick={handleCreate}
          disabled={isLoading}
          className="mt-6 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded transition-all duration-300"
        >
          <Save size={18} /> {isLoading ? 'CREATING...' : 'CREATE CAMPAIGN'}
        </button>
      </div>
    </div>
  );
};

export default NewCampaignModal;